import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import { GridRowModes } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import axios from "axios";
import { nanoid } from "nanoid";

import { API_URL, LIMIT } from "../../config/config.data";
import { dataActions } from "../../store";

export default function SelectProjectPeople({
  api,
  id,
  field,
  value,
  row,
  column,
  rowModesModel,
  setRowModesModel,
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const originalPeople = row.people.map(({ person, role }) => ({
    person,
    role,
    id: `${person.id}-${role.id}`,
  }));

  const [formData, setFormData] = useState(originalPeople);

  const [projectRoles, setProjectRoles] = useState([]);
  const [people, setPeople] = useState([]);

  // get project roles
  const fetchProjectRoles = async (abortController) => {
    try {
      const response = await axios.get(`${API_URL}/roles/project`, {
        signal: abortController.signal,
      });
      setProjectRoles([
        ...response.data.roles.sort((a, b) => a.name.localeCompare(b.name)),
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  // get people
  const fetchPeople = async (abortController) => {
    try {
      const response = await axios.get(`${API_URL}/people?limit=${LIMIT}`, {
        signal: abortController.signal,
      });

      setPeople([
        ...response.data.people.sort((a, b) =>
          a.last_name.localeCompare(b.last_name)
        ),
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  // get projects
  const getProjects = async () => {
    axios.get(`${API_URL}/projects?limit=${LIMIT}`).then((response) => {
      dispatch(
        dataActions.setData({
          data: response.data.projects,
          dataName: "projects",
        })
      );
    });
  };

  // add person to project
  const addPersonToProject = async (personId, roleId) => {
    try {
      axios
        .put(`${API_URL}/projects/${id}/people`, {
          person_id: personId,
          role_id: roleId,
        })
        .then((response) => {
          getProjects();
        })
        .catch((error) => {
          console.error("failed to add person to project", error);
        });
    } catch (error) {
      console.error("failed to add person to project", error);
    }
  };

  const deletePersonFromProject = async (personId) => {
    try {
      axios
        .delete(`${API_URL}/projects/${id}/people/${personId}`)
        .then((response) => {
          getProjects();
        })
        .catch((error) => {
          console.error("failed to delete person from project", error);
        });
    } catch (error) {
      console.error("failed to delete person from project", error);
    }
  };

  const updatePersonRole = async (personId, roleId) => {
    // delete and then add person because update is not supported
    try {
      axios
        .delete(`${API_URL}/projects/${id}/people/${personId}`)
        .then((response) => {
          addPersonToProject(personId, roleId);
        });
    } catch (error) {
      console.error("failed to update person role", error);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchProjectRoles(abortController);
    fetchPeople(abortController);
    return () => abortController.abort();
  }, []);

  const handleChangePerson = (newPersonId, id) => {
    setFormData((prev) =>
      [...prev].map((item) => {
        if (item.id === id) {
          return {
            ...item,
            person: people.find((person) => person.id === newPersonId),
          };
        }
        return item;
      })
    );
  };

  const handleChangeProjectRole = (itemId, newRoleId) => {
    setFormData((prev) =>
      [...prev].map((item) => {
        if (item.id === itemId) {
          return {
            ...item,
            role: {
              id: newRoleId,
              name: projectRoles.find((role) => role.id === newRoleId).name,
            },
          };
        }
        return item;
      })
    );
  };

  const handleAddPerson = () => {
    setFormData((prev) => {
      const existingPeopleIds = prev.map(({ person }) => person.id);
      const nextPerson = people.filter(
        (person) => !existingPeopleIds.includes(person.id)
      )[0];
      return [
        ...prev,
        {
          id: nanoid(),
          person: nextPerson,
          role: projectRoles[0],
        },
      ];
    });
  };

  const handleRemovePerson = (id) => {
    setFormData((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSubmitForm = async () => {
    // separate existing people from new people
    const existingPeople = formData.filter((item) =>
      originalPeople.map((item) => item.person.id).includes(item.person.id)
    );
    const newPeople = formData.filter(
      (item) =>
        !originalPeople.map((item) => item.person.id).includes(item.person.id)
    );
    const removePeople = originalPeople.filter(
      (item) =>
        !formData.map(({ person }) => person.id).includes(item.person.id)
    );

    // add new people to project
    newPeople.forEach(({ person, role }) => {
      addPersonToProject(person.id, role.id);
    });

    // remove people from project
    removePeople.forEach(({ person }) => {
      deletePersonFromProject(person.id);
    });

    // for existing people, check if role has changed
    existingPeople.forEach(({ person, role }) => {
      const originalRole = originalPeople.find(
        (item) => item.person.id === person.id
      ).role;
      if (role.id !== originalRole.id) {
        updatePersonRole(person.id, role.id);
      }
    });
    handleCloseDialog();
  };


  const handleCloseDialog = () => {
    setOpen(false);
    setRowModesModel({
      ...rowModesModel,
      [row.id]: { mode: GridRowModes.View },
    });
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Typography
          sx={{
            width: "200px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {formData
            ?.map(({ person }) => `${person.first_name} ${person.last_name}`)
            .join(", ") || ""}
        </Typography>
        <IconButton onClick={() => setOpen(true)}>
          <EditIcon />
        </IconButton>
      </Box>
      <Dialog open={open} onClose={handleCloseDialog}>
        <DialogTitle>{`Edit ${field}`}</DialogTitle>
        <DialogContent>
          <List disablePadding>
            {formData.map(({ person, role, id }) => (
              <ListItem
                key={id}
                sx={{ display: "flex", gap: 1 }}
                disableGutters
              >
                {people.length > 0 ? (
                  <Select
                    size="small"
                    value={person.id}
                    onChange={(e) => handleChangePerson(e.target.value, id)}
                    sx={{
                      minWidth: 250,
                    }}
                  >
                    {people.map((person) => (
                      <MenuItem
                        key={person.id}
                        value={person.id}
                        disabled={formData
                          .map((item) => item.person.id)
                          .includes(person.id)}
                      >
                        {person.first_name} {person.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Skeleton variant="rectangular" width={250} height={40} />
                )}
                {projectRoles.length > 0 ? (
                  <Select
                    size="small"
                    value={role.id}
                    onChange={(e) =>
                      handleChangeProjectRole(id, e.target.value)
                    }
                    sx={{
                      minWidth: 200,
                    }}
                  >
                    {projectRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Skeleton variant="rectangular" width={175} height={40} />
                )}
                <ListItemButton onClick={() => handleRemovePerson(id)}>
                  <DeleteIcon />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              // justifyContent: "right",
              my: 2,
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={handleAddPerson}
            >
              Add Person
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmitForm}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
